import React, { useMemo } from "react";
import GridRow from "./GridRow";

/**
 * GridBody
 * The `<tbody>` — emits one GridRow per visible row.  Hidden rows are
 * skipped here (returning null) so the rendered TR list lines up with
 * the freeze-pane offset measurement.
 *
 * PERF: the workbook border post-processing (keep bottom/right, keep
 * top/left only when no visible neighbour carries the edge) is done HERE,
 * once per sheet, in a useMemo — not per cell per render. This gives
 * every cell a STABLE style object identity, which is what lets
 * React.memo(GridCell) skip ~thousands of cell re-renders during
 * selection drags and scrolling.
 */
function GridBody({
  normalizedRows, rowHeights, colWidths,
  cellStyles, cellRichHtml, cellMerges, cellLinks,
  hiddenCols, hiddenRows,
  isRowVisible, isLastVisibleBeforeHidden, isLastVisibleBeforeHiddenRow,
  selectedCell, normalizedSelection, selectionType,
  frozenRows, frozenCols,
  measuredTheadHeight, frozenRowOffsets, frozenColOffsets,
  onSelectRow, onHeaderContextMenu, onStartResize,
  onStartSelection, onExtendSelection,
  onLinkClick, onCellContextMenu,
}) {
  // Workbook borders: the parser propagates every authored edge to BOTH
  // adjacent cells (a top border on r,c is also the bottom border of
  // r-1,c). Each shared edge must be painted exactly ONCE — keep each
  // cell's bottom/right border and drop top/left, except where no
  // visible neighbour exists to carry that edge (first row/column, or
  // hidden neighbour).
  const processedStyles = useMemo(() => {
    const out = {};
    for (const key in cellStyles) {
      const raw = cellStyles[key];
      if (!raw) continue;
      const { borderTop, borderLeft, ...clean } = raw;
      const sep = key.indexOf(":");
      const r = parseInt(key.slice(0, sep), 10);
      const c = parseInt(key.slice(sep + 1), 10);
      if (borderTop && (r === 0 || !isRowVisible(r - 1))) clean.borderTop = borderTop;
      if (borderLeft && (c === 0 || hiddenCols.has(c - 1))) clean.borderLeft = borderLeft;
      out[key] = clean;
    }
    return out;
  }, [cellStyles, hiddenCols, isRowVisible]);

  return (
    <tbody>
      {normalizedRows.map((row, rowIndex) => {
        if (!isRowVisible(rowIndex)) return null;
        const trClass = isLastVisibleBeforeHiddenRow(rowIndex) ? "hidden-row-after" : "";
        const isFrozenRow = rowIndex < frozenRows;
        const isLastFrozenRow = rowIndex === frozenRows - 1;

        return (
          <GridRow
            key={`row-${rowIndex}`}
            rowIndex={rowIndex}
            row={row}
            rowHeight={rowHeights[rowIndex]}
            trClassName={trClass}
            cellStyles={processedStyles}
            cellRichHtml={cellRichHtml}
            cellMerges={cellMerges}
            cellLinks={cellLinks}
            hiddenCols={hiddenCols}
            selectedCell={selectedCell}
            normalizedSelection={normalizedSelection}
            selectionType={selectionType}
            isLastVisibleBeforeHidden={isLastVisibleBeforeHidden}
            colWidths={colWidths}
            isFrozenRow={isFrozenRow}
            isLastFrozenRow={isLastFrozenRow}
            frozenCols={frozenCols}
            measuredTheadHeight={measuredTheadHeight}
            frozenRowOffset={frozenRowOffsets[rowIndex]}
            frozenColOffsets={frozenColOffsets}
            onSelectRow={onSelectRow}
            onHeaderContextMenu={onHeaderContextMenu}
            onStartResize={onStartResize}
            onStartSelection={onStartSelection}
            onExtendSelection={onExtendSelection}
            onLinkClick={onLinkClick}
            onCellContextMenu={onCellContextMenu}
          />
        );
      })}
    </tbody>
  );
}

export default React.memo(GridBody);
